import {
  COMPANIES,
  COMPANY_LABELS,
  SOCIAL_ACCOUNT_KINDS,
  SOCIAL_ACCOUNT_KIND_LABELS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORM_LABELS,
  type ChannelDto,
  type Company,
  type CreateSocialAccountInput,
  type SocialAccountDto,
  type SocialAccountKind,
  type SocialPlatform,
  type UserDto,
} from '@nova/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useFilter } from '../../app/FilterProvider.js';
import { Button } from '../../components/Button.js';
import { Modal } from '../../components/Modal.js';
import { api } from '../../lib/api.js';
import { useCreateAccount, useUpdateAccount } from './useSocial.js';

const fieldClass =
  'border-hairline bg-base/50 text-ink h-9 w-full rounded-lg border px-2.5 text-sm';
const labelClass = 'label-micro mb-1 block';

export function SocialAccountForm({
  open,
  onClose,
  account,
}: {
  open: boolean;
  onClose: () => void;
  /** Present → edit; absent → create. */
  account?: SocialAccountDto | null;
}) {
  const editing = !!account;
  const { areas } = useFilter();
  const { data: users = [] } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserDto[]>('/users'),
    staleTime: 5 * 60_000,
  });
  const { data: channels = [] } = useQuery({
    queryKey: ['channels'],
    queryFn: () => api.get<ChannelDto[]>('/channels'),
    staleTime: 5 * 60_000,
  });

  const create = useCreateAccount();
  const update = useUpdateAccount();

  const [platform, setPlatform] = useState<SocialPlatform>(account?.platform ?? 'LINKEDIN');
  const [kind, setKind] = useState<SocialAccountKind>(account?.kind ?? 'COMPANY');
  const [name, setName] = useState(account?.name ?? '');
  const [url, setUrl] = useState(account?.url ?? '');
  const [company, setCompany] = useState<Company>(account?.company ?? 'BEGGE');
  const [ownerUserId, setOwnerUserId] = useState(account?.ownerUserId ?? '');
  const [areaId, setAreaId] = useState(account?.areaId ?? '');
  const [channelId, setChannelId] = useState(account?.channelId ?? '');
  const [isActive, setIsActive] = useState(account?.isActive ?? true);
  const [note, setNote] = useState(account?.note ?? '');

  const pending = create.isPending || update.isPending;
  const error = (create.error ?? update.error) as Error | null;
  const valid = name.trim().length >= 2;

  async function submit() {
    const body: CreateSocialAccountInput = {
      platform,
      kind,
      name: name.trim(),
      url: url.trim() ? url.trim() : null,
      company,
      ownerUserId: ownerUserId || null,
      areaId: areaId || null,
      channelId: channelId || null,
      isActive,
      note: note.trim() ? note.trim() : null,
    };
    if (editing && account) {
      await update.mutateAsync({ id: account.id, body });
    } else {
      await create.mutateAsync(body);
    }
    onClose();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={editing ? 'Rediger konto' : 'Ny SoMe-konto'}
      subtitle={
        <p className="text-ink-soft text-sm">
          Overvåkning er atskilt fra publisering — kontoen kan valgfritt kobles til en
          publiseringskanal.
        </p>
      }
      footer={
        <div className="flex items-center justify-end gap-2">
          {error && <span className="text-danger mr-auto text-xs">{error.message}</span>}
          <Button size="sm" onClick={onClose}>
            Avbryt
          </Button>
          <Button
            size="sm"
            variant="primary"
            disabled={!valid}
            loading={pending}
            onClick={() => void submit()}
          >
            {editing ? 'Lagre' : 'Legg til'}
          </Button>
        </div>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label className={labelClass} htmlFor="some-platform">
            Plattform
          </label>
          <select
            id="some-platform"
            className={fieldClass}
            value={platform}
            onChange={(e) => setPlatform(e.target.value as SocialPlatform)}
          >
            {SOCIAL_PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {SOCIAL_PLATFORM_LABELS[p]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="some-kind">
            Type
          </label>
          <select
            id="some-kind"
            className={fieldClass}
            value={kind}
            onChange={(e) => setKind(e.target.value as SocialAccountKind)}
          >
            {SOCIAL_ACCOUNT_KINDS.map((k) => (
              <option key={k} value={k}>
                {SOCIAL_ACCOUNT_KIND_LABELS[k]}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="some-name">
            Navn
          </label>
          <input
            id="some-name"
            className={fieldClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="F.eks. Sem & Stenersen Prokom"
          />
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="some-url">
            Lenke (valgfritt)
          </label>
          <input
            id="some-url"
            className={fieldClass}
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.linkedin.com/company/…"
          />
        </div>

        <div>
          <label className={labelClass} htmlFor="some-company">
            Selskap
          </label>
          <select
            id="some-company"
            className={fieldClass}
            value={company}
            onChange={(e) => setCompany(e.target.value as Company)}
          >
            {COMPANIES.map((c) => (
              <option key={c} value={c}>
                {COMPANY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="some-owner">
            Ansvarlig (valgfritt)
          </label>
          <select
            id="some-owner"
            className={fieldClass}
            value={ownerUserId}
            onChange={(e) => setOwnerUserId(e.target.value)}
          >
            <option value="">— Ingen —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="some-area">
            Område (valgfritt)
          </label>
          <select
            id="some-area"
            className={fieldClass}
            value={areaId}
            onChange={(e) => setAreaId(e.target.value)}
          >
            <option value="">— Ingen —</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="some-channel">
            Publiseringskanal (valgfritt)
          </label>
          <select
            id="some-channel"
            className={fieldClass}
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
          >
            <option value="">— Ingen —</option>
            {channels.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelClass} htmlFor="some-note">
            Notat (valgfritt)
          </label>
          <textarea
            id="some-note"
            className="border-hairline bg-base/50 text-ink min-h-[64px] w-full rounded-lg border px-2.5 py-2 text-sm"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <label className="text-ink flex items-center gap-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="accent-accent h-4 w-4"
          />
          Aktiv (vises i overvåkningen)
        </label>
      </div>
    </Modal>
  );
}
